import {
  giftQueryOptions,
  giftSendQueryOptions,
  parseGiftStartParam,
  readOfflineVoucher,
  readOfflineVoucherBySend,
} from '@/entities/gift'
import { parseTicketStartParam, ticketQueryOptions } from '@/entities/ticket'
import { readOnboarded } from '@/entities/viewer'
import { getLanguage } from '@/shared/i18n'
import { ROUTES, buildPath } from '@/shared/config'
import { isObjectId } from '@/shared/lib'
import { queryClient } from './providers/query-client'

/**
 * Вход по карточке в чате и кнопкам бота (БП-6, БП-7, BE-53, BE-57).
 *
 * Ссылка `t.me/<бот>/app?startapp=…`, Telegram отдаёт её хвост в `start_param`
 * подписи (`entities/gift`, `parseGiftStartParam`):
 * - `s_<token>` — получателю: экран открытия `/open/:token`;
 * - `g_<id>` и голый id (старые кнопки) — дарителю: `/gifts/:id`;
 * - `t_<ticketId>` — автору обращения, «поддержка ответила» (BE-57): `/support/tickets/:id`.
 *
 * Здесь — до создания роутера и до первого рендера:
 * - адрес переписывается на экран подарка: роутер стартует сразу с него,
 *   витрина не грузится и не мелькает;
 * - запрос подарка уходит сразу, параллельно с загрузкой чанка, — первый
 *   экран получателя не ждёт ни того, ни другого по очереди.
 *
 * Только при свежем запуске (`isFreshCatalogLaunch`): `start_param` живёт всю
 * сессию Mini App, а перезагрузка WebView на витрине после «назад» не должна
 * снова уводить в подарок.
 * Вне Telegram (`?startapp=` в адресе, `mock-env.ts`) — то же самое: каждый
 * ввод адреса — свежий запуск, а перезагрузка экрана подарка остаётся на нём.
 *
 * Не наша ссылка — витрина как обычно (или онбординг, см. `applyOnboardingTarget`).
 *
 * Возвращает `true`, если адрес переписан на подарок или обращение.
 */
export const applyLaunchTarget = (startParam: string | null): boolean => {
  const ticketId = parseTicketStartParam(startParam)

  if (ticketId !== null) {
    return applyTicketTarget(ticketId)
  }

  const launch = parseGiftStartParam(startParam)

  if (!launch || !isFreshCatalogLaunch()) {
    return false
  }

  const language = getLanguage()
  let target: string

  // Ваучер сохранён на устройстве — сразу экран ваучера: без чанка распаковки и
  // без ожидания сети (на кассе её может не быть). Ответ бэкенда всё равно
  // придёт и, если подарок уже погашен или недоступен, поправит экран.
  if (launch.kind === 'send') {
    const offline = readOfflineVoucherBySend(launch.token)

    target = offline ? buildPath.voucher(offline.giftId) : buildPath.giftSend(launch.token)
    void queryClient.prefetchQuery(giftSendQueryOptions(launch.token, language))
  } else {
    target = readOfflineVoucher(launch.giftId) ? buildPath.voucher(launch.giftId) : buildPath.gift(launch.giftId)
    void queryClient.prefetchQuery(giftQueryOptions(launch.giftId, language))
  }

  replaceLaunchPath(target)

  return true
}

/**
 * Кнопка бота «Поддержка ответила» (BE-57, `t_<ticketId>`) — сразу экран
 * обращения, без онбординга, как вход в подарок. Чужое, несуществующее и
 * негодное обращение показывает он же: «Обращение не найдено».
 */
const applyTicketTarget = (ticketId: string): boolean => {
  if (!isFreshCatalogLaunch()) {
    return false
  }

  // `prefetchQuery` не смотрит на `enabled`: негодный id бэкенд отбил бы `400`, экран и так покажет «не найдено».
  if (isObjectId(ticketId)) {
    void queryClient.prefetchQuery(ticketQueryOptions(ticketId))
  }

  replaceLaunchPath(buildPath.supportTicket(ticketId))

  return true
}

/**
 * Свежий запуск на витрине: у записи истории, через которую уже прошёл
 * роутер, есть его `idx` — перезагрузка WebView после «назад» не должна
 * снова уводить ни в подарок, ни в онбординг.
 */
const isFreshCatalogLaunch = (): boolean => {
  const historyState = window.history.state as { idx?: unknown } | null

  return typeof historyState?.idx !== 'number' && window.location.pathname === ROUTES.CATALOG
}

/** Query и хэш — как есть: в них launch params клиента (`tgWebAppStartParam`, `tgWebAppData`). */
const replaceLaunchPath = (target: string): void =>
  window.history.replaceState(null, '', `${target}${window.location.search}${window.location.hash}`)

/**
 * Онбординг (экран 01) — один раз, при свежем запуске на витрине без подарка
 * (`applyLaunchTarget` не сработал). Получатель по `start_param` его не видит:
 * он сразу у своего подарка (CLAUDE.md, «Навигация»).
 *
 * Флаг `onboarded` — копия на устройстве (синхронно) или `CloudStorage`
 * Telegram (первый запуск на новом устройстве ждёт его не дольше 0,8 с), см.
 * `entities/viewer`. Здесь — до создания роутера: витрина не мелькает перед
 * онбордингом.
 */
export const applyOnboardingTarget = async (): Promise<void> => {
  if (!isFreshCatalogLaunch() || (await readOnboarded())) {
    return
  }

  // Пока ждали Telegram, адрес мог смениться только у нас самих — но проверим.
  if (isFreshCatalogLaunch()) {
    replaceLaunchPath(ROUTES.ONBOARDING)
  }
}
